import { useEffect, useRef } from 'react';
import { subscribeOnboardingEvent } from '@/components/onboarding/uiAdapter';
import {
  beginTourStage,
  endTourStage,
  commitTourStage,
  cleanupStaleTourLayers,
  type TourStagingSession,
} from '@/lib/tourStagingSession';
import { hasTourSeed } from '@/lib/tourSeedPresets';
import type { WorkflowState, MultiSheetWorkflowState } from '@/types/workflow';

interface UseTourStagingDeps {
  viewOnly: boolean;
  getActiveSheetState: () => WorkflowState;
  loadState: (data: WorkflowState | MultiSheetWorkflowState) => void;
  logAction: (verb: string, details?: string) => void;
}

/**
 * Tour-staging extraherad ur Index.tsx. Varje guided-flöde körs i en
 * TEMPORÄR layer (isTourSandbox). User's riktiga arbete rörs aldrig —
 * sandboxen försvinner vid `tour:end`. Om sessionen kraschar mid-tour
 * rensar `cleanupStaleTourLayers` den vid nästa mount.
 *
 * Hookar in tre onboarding-events:
 *   - `seed:request` — startar ny sandbox med tour-seed:ade blocks
 *   - `tour:end`     — rensar sandbox + återställer user-state
 *   - `tour:commit`  — behåller sandbox som vanlig layer
 *
 * Plus en mount-cleanup som körs en gång vid sid-mount och rensar
 * övergivna sandbox:ar från föregående crashed session.
 */
export function useTourStaging(deps: UseTourStagingDeps) {
  const { viewOnly, getActiveSheetState, loadState, logAction } = deps;
  const tourSessionRef = useRef<TourStagingSession | null>(null);

  useEffect(() => {
    if (viewOnly) return;
    return subscribeOnboardingEvent((event) => {
      if (event.name === 'seed:request') {
        const section = (event.data as { section?: string } | undefined)?.section;
        if (!section || !hasTourSeed(section)) return;
        if (tourSessionRef.current) return; // redan aktiv
        const current = getActiveSheetState();
        const { state: staged, session } = beginTourStage(current, section);
        tourSessionRef.current = session;
        loadState(staged);
        logAction('Tour sandbox seeded', `section=${section}`);
        return;
      }

      if (event.name === 'tour:end') {
        const session = tourSessionRef.current;
        if (!session) return;
        const { state: restored } = endTourStage(session);
        loadState(restored);
        tourSessionRef.current = null;
        logAction('Tour sandbox removed', 'user workflow restored');
        return;
      }

      if (event.name === 'tour:commit') {
        const session = tourSessionRef.current;
        if (!session) return;
        const current = getActiveSheetState();
        const { state: kept } = commitTourStage(current, session);
        loadState(kept);
        tourSessionRef.current = null;
        logAction('Tour sandbox committed', 'kept as regular layer');
        return;
      }
    });
  }, [viewOnly, getActiveSheetState, loadState, logAction]);

  // Auto-cleanup från föregående session (crash / hard-refresh mid-tour).
  useEffect(() => {
    if (viewOnly) return;
    const current = getActiveSheetState();
    const hasStale = current.groups.some(g => g.isTourSandbox);
    if (hasStale) {
      const cleaned = cleanupStaleTourLayers(current);
      loadState(cleaned);
      logAction('Stale tour sandbox cleaned', 'from previous session');
    }
    // Run only once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
